// Pixel-art storage.
//
// A drawing is a run-length-encoded string of palette indices: "0x120,3x8,...".
// Not an image -- a 32x32 grid is a few hundred bytes this way, needs no binary
// handling anywhere in the stack, and is legible in a database dump.

/** Index 0 is "empty" and renders as the page background, not as a colour. */
export const PALETTE = [
  'transparent',
  '#000000',
  '#ffffff',
  '#6b7280',
  '#ef4444',
  '#f97316',
  '#eab308',
  '#84cc16',
  '#22c55e',
  '#14b8a6',
  '#06b6d4',
  '#3b82f6',
  '#6366f1',
  '#a855f7',
  '#ec4899',
  '#92400e',
]

export const GRID = 32

export function blankCanvas(size = GRID): number[] {
  return Array<number>(size * size).fill(0)
}

export function encode(pixels: number[]): string {
  if (pixels.length === 0) return ''
  const runs: string[] = []
  let value = pixels[0]
  let count = 1

  for (let i = 1; i < pixels.length; i++) {
    if (pixels[i] === value) {
      count++
    } else {
      runs.push(`${value}x${count}`)
      value = pixels[i]
      count = 1
    }
  }
  runs.push(`${value}x${count}`)
  return runs.join(',')
}

export function decode(encoded: string, expected: number): number[] {
  const pixels: number[] = []
  if (encoded) {
    for (const run of encoded.split(',')) {
      const [rawValue, rawCount] = run.split('x')
      const value = Number(rawValue)
      const count = Number(rawCount)
      if (!Number.isFinite(value) || !Number.isFinite(count)) continue
      for (let i = 0; i < count; i++) pixels.push(value)
    }
  }
  // Pad or trim: a corrupted string should still render something rather than
  // crashing the gallery.
  while (pixels.length < expected) pixels.push(0)
  return pixels.slice(0, expected)
}

/**
 * Flood fill from one cell, four-way.
 *
 * Iterative rather than recursive: filling a blank 32x32 canvas is 1024 cells
 * deep and would risk exhausting the stack.
 */
export function floodFill(
  pixels: number[],
  size: number,
  start: number,
  color: number,
): number[] {
  const target = pixels[start]
  if (target === color) return pixels

  const next = [...pixels]
  const stack = [start]

  while (stack.length > 0) {
    const at = stack.pop()!
    if (next[at] !== target) continue
    next[at] = color

    const x = at % size
    const y = Math.floor(at / size)
    if (x > 0) stack.push(at - 1)
    if (x < size - 1) stack.push(at + 1)
    if (y > 0) stack.push(at - size)
    if (y < size - 1) stack.push(at + size)
  }
  return next
}

/** Render a drawing as an inline SVG data URI, for thumbnails. */
export function toDataUri(pixels: number[], size: number): string {
  const rects: string[] = []
  pixels.forEach((value, i) => {
    if (value === 0) return
    const x = i % size
    const y = Math.floor(i / size)
    rects.push(`<rect x="${x}" y="${y}" width="1" height="1" fill="${PALETTE[value]}"/>`)
  })
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${size} ${size}" shape-rendering="crispEdges">${rects.join('')}</svg>`
  return `data:image/svg+xml,${encodeURIComponent(svg)}`
}
